// ============================================================
// src/pages/MyHistoryPage.jsx
// ============================================================
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getMyHistory, getWeekEntries } from '../api/timesheetApi';
import { Toast, StatCard, StatusBadge, Spinner, CardTitle, PageHeader, Modal } from '../components/UI';
import { useToast } from '../hooks/useToast';
import { formatFromDate, parseDate } from '../utils/dateHelper';

export default function MyHistoryPage() {
  const { toast, showToast, clearToast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // const [filters, setFilters] = useState({ status: '', year: '' });
  // Update filters state
  const [filters, setFilters] = useState({ status: '', year: '', from: '', to: '' });
  const [selected, setSelected] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loadingEnt, setLoadingEnt] = useState(false);

  const toHourMinuteString = totalMinutes => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}.${String(minutes).padStart(2, '0')}`;
  };

  function to12Hour(timeStr) {
    if (!timeStr) return '—';
    const [h, m] = timeStr.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return '—';
    const period = h >= 12 ? 'PM' : 'AM';
    const hour = h % 12 || 12;
    return `${hour}:${String(m).padStart(2, '0')} ${period}`;
  }

  const toMinutes = value => {
    if (value === null || value === undefined || value === '') return 0;
    const [hPart, mPart = '00'] = String(value).split('.');
    const h = Number(hPart);
    const m = Number((mPart || '00').padEnd(2, '0').slice(0, 2));
    if (Number.isNaN(h) || Number.isNaN(m)) return 0;
    return (h * 60) + Math.min(Math.max(m, 0), 59);
  };

  // Add this helper above return()
  function setThisWeek() {
    const today = new Date();
    const day = today.getDay(); // 0=Sun
    const mon = new Date(today);
    mon.setDate(today.getDate() - ((day + 6) % 7));
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);
    const fmt = d => d.toISOString().slice(0, 10);
    setFilters(f => ({ ...f, from: fmt(mon), to: fmt(sun) }));
  }

  // const fetchHistory = useCallback(async () => {
  //   setLoading(true);
  //   try {
  //     const data = await getMyHistory(filters);
  //     setRows(data);
  //   } catch (err) {
  //     showToast(err.message || 'Failed to load history', 'error');
  //   } finally {
  //     setLoading(false);
  //   }
  // }, [filters, showToast]);

  const fetchHistory = useCallback(async (f) => {
    const activeFilters = f || filters;
    setLoading(true);
    try {
      const data = await getMyHistory(activeFilters);
      setRows(data);
    } catch (err) {
      showToast(err.message || 'Failed to load history', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchHistory(); }, []); // only on mount

  async function viewEntries(week) {
    setSelected(week);
    setLoadingEnt(true);
    try {
      const data = await getWeekEntries(week.id);
      setEntries(data);
    } catch {
      setEntries([]);
    } finally {
      setLoadingEnt(false);
    }
  }

  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  function DatePicker({ value, onChange, small = false, inputStyle = {} }) {
    const selected = parseDate(value);
    const today = new Date();
    const [open, setOpen] = useState(false);
    const [viewYear, setViewYear] = useState((selected || today).getFullYear());
    const [viewMonth, setViewMonth] = useState((selected || today).getMonth());
    const wrapRef = useRef(null);

    useEffect(() => {
      if (!open) return;
      const handle = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
      document.addEventListener('mousedown', handle);
      return () => document.removeEventListener('mousedown', handle);
    }, [open]);

    useEffect(() => {
      const d = parseDate(value);
      if (d) { setViewYear(d.getFullYear()); setViewMonth(d.getMonth()); }
    }, [value]);

    const prevMonth = () => viewMonth === 0 ? (setViewMonth(11), setViewYear(y => y - 1)) : setViewMonth(m => m - 1);
    const nextMonth = () => viewMonth === 11 ? (setViewMonth(0), setViewYear(y => y + 1)) : setViewMonth(m => m + 1);

    function buildDays() {
      const first = new Date(viewYear, viewMonth, 1).getDay();
      const total = new Date(viewYear, viewMonth + 1, 0).getDate();
      const cells = [];
      for (let i = 0; i < first; i++) cells.push(null);
      for (let d = 1; d <= total; d++) cells.push(d);
      return cells;
    }

    const isSelected = d => d && selected &&
      selected.getFullYear() === viewYear && selected.getMonth() === viewMonth && selected.getDate() === d;
    const isToday = d => d &&
      today.getFullYear() === viewYear && today.getMonth() === viewMonth && today.getDate() === d;

    function selectDay(day) {
      if (!day) return;
      onChange(formatFromDate(new Date(viewYear, viewMonth, day)));
      setOpen(false);
    }

    const years = [];
    for (let y = today.getFullYear() - 5; y <= today.getFullYear() + 5; y++) years.push(y);

    return (
      <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
        {/* Trigger */}
        <div style={{ position: 'relative', width: '100%' }}>
          <input
            type="text" readOnly
            value={value || ''}
            placeholder="yyyy-mm-dd"
            onClick={() => setOpen(o => !o)}
            style={{
              width: '100%', boxSizing: 'border-box',
              border: `1.5px solid ${open ? '#dc2626' : '#d1d5db'}`,
              borderRadius: '6px',
              padding: small ? '0.28rem 2.2rem 0.28rem 0.55rem' : '0.48rem 2.4rem 0.48rem 0.75rem',
              fontSize: small ? '0.8rem' : '0.875rem',
              color: value ? '#111827' : '#9ca3af',
              background: '#fff', outline: 'none', cursor: 'pointer',
              transition: 'border-color 0.15s',
              ...inputStyle,   // 👈 add this
            }}
          />
          <span onClick={() => setOpen(o => !o)} style={{
            position: 'absolute', right: '0.55rem', top: '50%',
            transform: 'translateY(-50%)',
            fontSize: small ? '0.9rem' : '1rem',
            cursor: 'pointer', userSelect: 'none', lineHeight: 1,
          }}>📅</span>
        </div>

        {/* Popup — fixed, auto-flips */}
        {open && (
          <div
            style={{
              position: 'fixed', top: 0, left: 0,
              zIndex: 99999, background: '#fff',
              border: '1px solid #e5e7eb', borderRadius: '12px',
              boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
              width: '288px', userSelect: 'none',
            }}
            ref={el => {
              if (!el || !wrapRef.current) return;
              const rect = wrapRef.current.getBoundingClientRect();
              const popH = el.offsetHeight || 300;
              const below = window.innerHeight - rect.bottom;
              el.style.top = below >= popH + 8 || below >= rect.top
                ? `${rect.bottom + 6}px`
                : `${rect.top - popH - 6}px`;
              let left = rect.left;
              if (left + 288 > window.innerWidth - 8) left = window.innerWidth - 296;
              if (left < 8) left = 8;
              el.style.left = `${left}px`;
            }}
          >
            {/* Header */}
            <div style={{ padding: '12px 14px 8px', borderBottom: '1px solid #f3f4f6' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <button onClick={prevMonth} style={navBtn}>‹</button>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <select value={viewMonth} onChange={e => setViewMonth(+e.target.value)} style={dropStyle}>
                    {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
                  </select>
                  <select value={viewYear} onChange={e => setViewYear(+e.target.value)} style={dropStyle}>
                    {years.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <button onClick={nextMonth} style={navBtn}>›</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '2px' }}>
                {DAYS.map(d => (
                  <div key={d} style={{ textAlign: 'center', fontSize: '0.62rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', lineHeight: '26px' }}>{d}</div>
                ))}
              </div>
            </div>

            {/* Days grid */}
            <div style={{ padding: '8px 10px 12px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '2px' }}>
                {buildDays().map((day, i) => (
                  <div key={i} onClick={() => selectDay(day)} style={{
                    height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: '7px', fontSize: '0.82rem',
                    cursor: day ? 'pointer' : 'default',
                    fontWeight: isSelected(day) || isToday(day) ? 700 : 400,
                    background: isSelected(day) ? '#dc2626' : 'transparent',
                    color: isSelected(day) ? '#fff' : isToday(day) ? '#dc2626' : day ? '#374151' : 'transparent',
                    transition: 'background 0.1s, color 0.1s',
                  }}
                    onMouseEnter={e => { if (day && !isSelected(day)) { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#dc2626'; } }}
                    onMouseLeave={e => { if (day && !isSelected(day)) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = isToday(day) ? '#dc2626' : '#374151'; } }}
                  >{day || ''}</div>
                ))}
              </div>
              {/* Today shortcut */}
              <div style={{ textAlign: 'center', marginTop: '8px', borderTop: '1px solid #f3f4f6', paddingTop: '8px' }}>
                <button onClick={() => { onChange(getTodayMDY()); setOpen(false); }} style={{
                  background: 'none', border: '1px solid #e5e7eb', borderRadius: '5px',
                  padding: '3px 14px', fontSize: '0.75rem', fontWeight: 600,
                  color: '#374151', cursor: 'pointer',
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#dc2626'; e.currentTarget.style.color = '#dc2626'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#374151'; }}
                >Today</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  const navBtn = { background: 'none', border: '1px solid #e5e7eb', borderRadius: '5px', width: '28px', height: '28px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', color: '#6b7280', lineHeight: 1 };
  const dropStyle = { fontSize: '0.78rem', fontWeight: 600, border: '1px solid #e5e7eb', borderRadius: '5px', padding: '3px 6px', color: '#374151', background: '#f9fafb', outline: 'none', cursor: 'pointer' };



  const stats = {
    total: rows.length,
    approved: rows.filter(r => r.status === 'approved').length,
    pending: rows.filter(r => r.status === 'submitted').length,
    hrs: toHourMinuteString(rows.reduce((s, r) => s + toMinutes(r.total_hours || 0), 0)),
  };

  const years = [...new Set(rows.map(r => r.week_ending_date?.slice(0, 4)))].filter(Boolean).sort().reverse();

  return (
    <div>
      <PageHeader title="MY TIMESHEET HISTORY" subtitle="All submitted weeks" />

      {/* <div className="stat-grid mb-4">
        <StatCard label="Total Weeks" value={stats.total}    color="gray" />
        <StatCard label="Approved"    value={stats.approved} color="green" />
        <StatCard label="Pending"     value={stats.pending}  color="yellow" />
        <StatCard label="Total Hours" value={stats.hrs}      sub="all time" color="red" />
      </div> */}

      <div className="filter-row mb-3" style={{ alignItems: 'center' }}>

        {/* STATUS — call API on change */}
        <select className="sel" style={{ width: 'auto' }} value={filters.status}
          onChange={e => {
            const updated = { ...filters, status: e.target.value };
            setFilters(updated);
            fetchHistory(updated);
          }}>
          <option value="">All Statuses</option>
          <option value="draft">Pending</option>
          <option value="submitted">Submitted</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>

        {/* YEAR / THIS WEEK — call API on change */}
        <select
          className="sel"
          style={{ width: 'auto' }}
          value={filters.year}
          onChange={e => {
            const val = e.target.value;
            if (val === 'this_week') {
              const today = new Date();
              const day = today.getDay();
              const sun = new Date(today);
              sun.setDate(today.getDate() - day);
              const sat = new Date(sun);
              sat.setDate(sun.getDate() + 6);
              const fmt = formatFromDate;
              const updated = { ...filters, year: 'this_week', from: fmt(sun), to: fmt(sat) };
              setFilters(updated);
              fetchHistory(updated);
            } else {
              const updated = { ...filters, year: val, from: '', to: '' };
              setFilters(updated);
              fetchHistory(updated);
            }
          }}
        >
          <option value="">All Years</option>
          <option value="this_week">This Week</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>

        {/* FROM — only set state, NO API call */}
        <div style={{ width: '155px' }}>
          <DatePicker
            value={filters.from}
            onChange={val => {
              setFilters(f => ({ ...f, from: val, year: '', to: '' }));
              // ❌ No API call here
            }}
            inputStyle={{
              padding: '0.48rem 2.4rem 0.48rem 0.75rem',
              fontSize: '0.875rem',
              borderRadius: '6px',
            }}
          />
        </div>

        {/* TO — set state AND call API only if from is also set */}
        <div style={{ width: '155px' }}>
          <DatePicker
            value={filters.to}
            onChange={val => {
              const updated = { ...filters, to: val, year: '' };
              setFilters(updated);
              if (filters.from && val) {
                fetchHistory(updated); // ✅ API call only when both from & to selected
              }
            }}
            inputStyle={{
              padding: '0.48rem 2.4rem 0.48rem 0.75rem',
              fontSize: '0.875rem',
              borderRadius: '6px',
            }}
          />
        </div>

        {/* REFRESH */}
        <button
          className="btn btn-secondary btn-sm"
          style={{ padding: '0.48rem 0.75rem', fontSize: '0.875rem', whiteSpace: 'nowrap' }}
          onClick={() => fetchHistory()}
        >
          REFRESH
        </button>

      </div>

      <div className="card">
        <CardTitle>Timesheet Weeks</CardTitle>
        {loading ? <Spinner /> : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  {['Week Ending', 'Hrs', 'Regular', 'OT', 'Status', 'Reviewed By', 'Notes', ''].map(h => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0
                  ? <tr><td colSpan={8} className="tbl-empty">No history found.</td></tr>
                  : rows.map(r => (
                    <tr key={r.id}>
                      <td className="td-mono">{r.week_ending_date}</td>
                      <td><strong>{toHourMinuteString(toMinutes(r.total_hours))}</strong></td>
                      <td className="td-mono td-muted">{toHourMinuteString(toMinutes(r.regular_hours))}</td>
                      <td>
                        {toMinutes(r.overtime_hours) > 0
                          ? <span className="td-red">{toHourMinuteString(toMinutes(r.overtime_hours))}</span>
                          : <span className="td-muted">—</span>}
                      </td>
                      <td><StatusBadge status={r.status} /></td>
                      <td className="td-muted">{r.reviewer_name || '—'}</td>
                      <td className="td-trunc" style={{ maxWidth: '10rem' }}>
                        {r.reviewer_notes
                          ? <span className="italic text-muted" style={{ fontSize: '0.75rem' }}>{r.reviewer_notes}</span>
                          : <span className="td-muted">—</span>}
                      </td>
                      <td>
                        <button className="btn btn-secondary btn-xs" onClick={() => viewEntries(r)}>VIEW</button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal open={!!selected}
        title={`Entries – Week Ending ${selected?.week_ending_date || ''}`}
        onClose={() => setSelected(null)} wide>
        {loadingEnt ? <Spinner /> : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  {['Date', 'WO #', 'Job #','Loc', 'Description', 'In', 'Out', 'Hrs'].map(h => <th key={h}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {entries.length === 0
                  ? <tr><td colSpan={7} className="tbl-empty">No entries.</td></tr>
                  : entries.map(en => (
                    <tr key={en.id}>
                      <td className="td-mono">{en.entry_date}</td>
                      <td className="td-mono" style={{ color: '#7c3aed' }}>{en.work_order || '—'}</td>
                      <td className="td-mono td-red">{en.job_number}</td>
                      <td className="td-mono td-red">{en.location}</td>
                      <td className="td-trunc">{en.description}</td>
                      <td className="td-mono td-green">{en.time_in?.slice(0, 5)}</td>
                      <td className="td-mono td-red">{en.time_out?.slice(0, 5)}</td>
                      <td><strong>{toHourMinuteString(toMinutes(en.hours_worked))}h</strong></td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      <Toast message={toast.message} type={toast.type} onClose={clearToast} />
    </div>
  );
}
